import {Component, OnInit, ChangeDetectionStrategy} from "@angular/core";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {HttpErrorResponse} from "@angular/common/http";
import {NzMessageService} from "ng-zorro-antd/message";
import {lastValueFrom} from "rxjs";
import {PerFormaAPIService, LoginRequest} from "src/app/services/pfapi.service";
import {AuthService} from "src/app/services/auth.service";

@Component({
    selector: "app-login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginComponent implements OnInit {
    form: FormGroup;
    submitting = false;
    errorMessage = "";
    // neutral prompt (e.g. "enter your 2FA code") shown without the red error styling
    infoMessage = "";
    year = new Date().getFullYear();
    // shown once the backend reports a 2FA code is required for this account
    twoFactorRequired = false;

    private returnUrl = "/";

    constructor(
        private readonly fb: FormBuilder,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly api: PerFormaAPIService,
        private readonly message: NzMessageService,
        private readonly authService: AuthService
    ) {
        this.form = this.fb.group({
            username: ["", [Validators.required]],
            password: ["", [Validators.required]],
            twoFactorCode: [""]
        });
    }

    ngOnInit(): void {
        this.returnUrl = this.route.snapshot.queryParamMap.get("returnUrl") || "/";
    }

    async onSubmit(): Promise<void> {
        this.errorMessage = "";
        this.infoMessage = "";

        if (this.form.invalid) {
            Object.keys(this.form.controls).forEach(k => this.form.get(k)?.markAsTouched());
            return;
        }

        // NOTE: the Identity login endpoint's field is `email`, but PerForma users sign in by
        // username — we deliberately send the entered identifier in the `email` field.
        const twoFactorCode = (this.form.value.twoFactorCode || "").trim();
        const request: LoginRequest = {
            Email: this.form.value.username,
            Password: this.form.value.password,
            TwoFactorCode: twoFactorCode || undefined
        };

        this.submitting = true;
        try {
            const result = await lastValueFrom(this.api.postApiAuthLogin(request));
            localStorage.setItem("pf_token", result.AccessToken);
            if (result.RefreshToken) localStorage.setItem("pf_refresh", result.RefreshToken);

            // load the new user's session/profile so we can honor forced password / email steps
            await this.authService.refresh();
            if (this.authService.mustChangePass) {
                await this.router.navigateByUrl("/profile/password");
                return;
            }
            if (this.authService.mustSetEmail) {
                await this.router.navigateByUrl("/profile/email");
                return;
            }
            // pending consent forms (e.g. AI opt-in) must be answered before continuing
            if (this.authService.needsConsent) {
                await this.router.navigateByUrl("/account/consent");
                return;
            }

            await this.router.navigateByUrl(this.returnUrl);
        } catch (err) {
            this.handleError(err);
        } finally {
            this.submitting = false;
        }
    }

    private handleError(err: unknown): void {
        if (err instanceof HttpErrorResponse && err.status === 401) {
            const detail = (err.error && (err.error.detail || err.error.title)) || "";
            if (typeof detail === "string" && detail.toLowerCase().includes("twofactor")) {
                // backend wants a 2FA code — reveal the field and prompt (neutral, not an error)
                this.twoFactorRequired = true;
                this.infoMessage = "Enter your two-factor authentication code.";
                return;
            }
            this.errorMessage = "Invalid username or password.";
            return;
        }

        this.errorMessage = "Unable to sign in. Please try again.";
        this.message.error(this.errorMessage);
    }
}
